import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_JULIA_C } from './fractalPresets';
import { EXPLORER_PALETTE, samplePalette, withPaletteTheme } from './fractalPalette';
import { computeInverseJuliaPreimages, computeJuliaOrbitDensityGrid } from './fractalIteration';
import { PARAMETER_PRESETS } from './JuliaParameterExplorer';

const VIEW_RANGE = Object.freeze([-1.75, 1.75]);
const CANVAS_SIZE = 280;

export function InverseIterationExplorer({ paletteTheme }) {
  const [mode, setMode] = useState('inverse');
  const [juliaC, setJuliaC] = useState(() => [...DEFAULT_JULIA_C]);
  const canvasRef = useRef(null);

  const themedPalette = useMemo(
    () => withPaletteTheme({ ...EXPLORER_PALETTE, gScale: 9, juliaC }, paletteTheme),
    [paletteTheme, juliaC]
  );

  const preimageCoords = useMemo(
    () => (mode === 'inverse' ? computeInverseJuliaPreimages(juliaC, 16384) : null),
    [mode, juliaC]
  );

  const densityGrid = useMemo(
    () => (mode === 'density'
      ? computeJuliaOrbitDensityGrid(CANVAS_SIZE, CANVAS_SIZE, VIEW_RANGE, VIEW_RANGE, juliaC, 48)
      : null),
    [mode, juliaC]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !canvas.getContext?.mock) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx || typeof ctx.createImageData !== 'function') return;
    const w = canvas.width;
    const h = canvas.height;
    const imageData = ctx.createImageData(w, h);
    if (!imageData?.data) return;
    const data = imageData.data;
    for (let p = 0; p < data.length; p += 4) {
      data[p] = 10;
      data[p + 1] = 14;
      data[p + 2] = 24;
      data[p + 3] = 255;
    }
    const rgba = [0, 0, 0, 255];
    if (mode === 'inverse' && preimageCoords) {
      const span = VIEW_RANGE[1] - VIEW_RANGE[0];
      const count = preimageCoords.length >> 1;
      for (let i = 0; i < count; i++) {
        const x = preimageCoords[i * 2];
        const y = preimageCoords[i * 2 + 1];
        const px = Math.floor(((x - VIEW_RANGE[0]) / span) * w);
        const py = Math.floor(((y - VIEW_RANGE[0]) / span) * h);
        if (px < 0 || px >= w || py < 0 || py >= h) continue;
        samplePalette(18 + (i % 48), themedPalette, rgba);
        const idx = (py * w + px) * 4;
        data[idx] = Math.min(255, data[idx] + Math.round(rgba[0] * 0.55));
        data[idx + 1] = Math.min(255, data[idx + 1] + Math.round(rgba[1] * 0.55));
        data[idx + 2] = Math.min(255, data[idx + 2] + Math.round(rgba[2] * 0.55));
      }
    } else if (mode === 'density' && densityGrid && densityGrid.maxCount > 0) {
      const logMax = Math.log1p(densityGrid.maxCount);
      const counts = densityGrid.counts;
      for (let i = 0; i < counts.length; i++) {
        const cVal = counts[i];
        if (cVal === 0) continue;
        const normalized = Math.log1p(cVal) / logMax;
        samplePalette(normalized * 64, themedPalette, rgba);
        const idx = i * 4;
        data[idx] = rgba[0];
        data[idx + 1] = rgba[1];
        data[idx + 2] = rgba[2];
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }, [mode, preimageCoords, densityGrid, themedPalette]);

  const sampleSummary = mode === 'inverse'
    ? `${(preimageCoords ? preimageCoords.length / 2 : 0).toLocaleString()} backward square-root preimages`
    : `Escaping orbit visit density (peak ${densityGrid?.maxCount ?? 0})`;

  return (
    <div className="orbit-explorer" data-testid="inverse-iteration-explorer" data-palette-theme={paletteTheme || 'amethyst'}>
      <div className="orbit-explorer-header">
        <span className="orbit-explorer-title">Inverse Iteration &amp; Orbit Density</span>
        <span className="orbit-explorer-readout" data-testid="inverse-explorer-summary">
          {sampleSummary}
        </span>
      </div>
      <div className="orbit-explorer-presets" role="group" aria-label="Alternative rendering method">
        <button
          type="button"
          className={`parameter-preset-pill${mode === 'inverse' ? ' active' : ''}`}
          data-render-mode="inverse"
          aria-pressed={mode === 'inverse'}
          onClick={() => setMode('inverse')}
        >
          Inverse Iteration (Preimage Tree)
        </button>
        <button
          type="button"
          className={`parameter-preset-pill${mode === 'density' ? ' active' : ''}`}
          data-render-mode="density"
          aria-pressed={mode === 'density'}
          onClick={() => setMode('density')}
        >
          Orbit Density (Nebulabrot)
        </button>
      </div>
      <div className="orbit-explorer-presets" role="group" aria-label="Parameter presets for inverse iteration">
        {PARAMETER_PRESETS.map(preset => {
          const active = Math.abs(juliaC[0] - preset.c[0]) < 1e-4 && Math.abs(juliaC[1] - preset.c[1]) < 1e-4;
          return (
            <button
              key={preset.id}
              type="button"
              className={`parameter-preset-pill${active ? ' active' : ''}`}
              data-inverse-preset={preset.id}
              aria-pressed={active}
              onClick={() => setJuliaC([...preset.c])}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="orbit-explorer-body">
        <div className="orbit-canvas-stage">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            aria-label="Inverse iteration and orbit density canvas"
          />
        </div>
        <div className="orbit-telemetry">
          <div className="orbit-telemetry-row">
            <span>Method</span>
            <strong>{mode === 'inverse' ? 'Backward Preimages' : 'Escaping Trajectory Density'}</strong>
          </div>
          <div className="orbit-telemetry-row">
            <span>Parameter c</span>
            <code>
              {juliaC[0].toFixed(4)} {juliaC[1] >= 0 ? '+' : '\u2212'} {Math.abs(juliaC[1]).toFixed(4)}i
            </code>
          </div>
          <p className="orbit-telemetry-note">
            {mode === 'inverse'
              ? (
                <>
                  Because the Julia boundary J<sub>c</sub> is backward-invariant,
                  repeatedly taking both complex square roots z<sub>n&minus;1</sub> =
                  {' '}&plusmn;&radic;(z<sub>n</sub> &minus; c) from a repelling
                  fixed point lands directly on J<sub>c</sub>.
                </>
              )
              : 'Orbit-density rendering accumulates a 2D histogram of every intermediate point visited by escaping trajectories before they cross |z| = 2, revealing glowing harmonic filaments.'}
          </p>
        </div>
      </div>
    </div>
  );
}
